/** Must stay in sync with the horizontal tabs `gap` in `Tabs.styles.ts`. */
export const TABS_HORIZONTAL_GAP_PX = 20;

export type ComputeMaxVisibleTabsCountParams = {
  containerWidth: number;
  gap: number;
  seeMoreWidth: number;
  tabWidths: number[];
};

/**
 * Computes how many tabs fit in the container before overflowing into "See more".
 * @param params.containerWidth Available width of the tabs container.
 * @param params.gap Horizontal gap between tabs.
 * @param params.seeMoreWidth Width reserved for the "See more" control when overflow exists.
 * @param params.tabWidths Measured width of each tab, in display order.
 * @returns The number of tabs that can be shown without overflowing.
 */
export function computeMaxVisibleTabsCount({
  containerWidth,
  gap,
  seeMoreWidth,
  tabWidths,
}: ComputeMaxVisibleTabsCountParams): number {
  if (containerWidth <= 0 || tabWidths.length === 0) {
    return tabWidths.length;
  }

  const totalWidth = tabWidths.reduce(
    (sum, tabWidth, index) => sum + tabWidth + (index > 0 ? gap : 0),
    0,
  );

  if (totalWidth <= containerWidth) {
    return tabWidths.length;
  }

  let usedWidth = 0;
  let visibleCount = 0;

  for (let index = 0; index < tabWidths.length; index++) {
    const tabWidth = tabWidths[index];
    const widthWithGap = tabWidth + (visibleCount > 0 ? gap : 0);
    const hasOverflowAfter = index < tabWidths.length - 1;
    const reservedSeeMoreWidth = hasOverflowAfter ? seeMoreWidth : 0;

    if (usedWidth + widthWithGap + reservedSeeMoreWidth <= containerWidth) {
      usedWidth += widthWithGap;
      visibleCount += 1;
      continue;
    }

    break;
  }

  return Math.max(1, visibleCount);
}
